import { useState, useEffect } from 'react';

// Define the structure for UI preferences
interface UIPreferences {
  projectsViewMode: 'grid' | 'list';
  specsViewMode: 'grid' | 'list';
  artifactsViewMode: 'grid' | 'list';
  personasViewMode: 'grid' | 'list';
  theme: 'light' | 'dark' | 'system';
  sidebarCollapsed: boolean;
  layoutMode: 'standard' | 'wide';
  jsonExpandedByDefault: boolean;
  /**
   * Dismissal ledger for one-off onboarding/explainer panels (task 526),
   * keyed by a stable panel key. Absent key = not dismissed. Kept as one map
   * rather than a boolean per panel so new panels need no preference schema
   * change.
   */
  dismissedPanels: Record<string, boolean>;
  /**
   * Legacy browser-wide Assistant sidebar open/collapsed state (task 720).
   * No longer read (task 1051): kept only so stored records still parse. The
   * record is shared by every account that signs in from the browser, so one
   * User's collapsed rail used to follow the next account. Deliberately not
   * migrated into `assistantSidebarByUser`.
   */
  assistantSidebar: 'open' | 'collapsed';
  /**
   * Assistant sidebar open/collapsed state per User (task 1051), keyed by the
   * Keycloak subject. Absent entry = open. Desktop-only concept — on tablet
   * and mobile the panel is simply open or closed for the session, and
   * neither presentation writes this preference.
   */
  assistantSidebarByUser: Record<string, 'open' | 'collapsed'>;
  /** Docked sidebar width in px, clamped to the drag range at read time. */
  assistantSidebarWidth: number;
  /**
   * Legacy browser-wide History section collapsed flag (task 1202). No longer
   * read (task 1210): kept only so stored records still parse. Like
   * `assistantSidebar`, it was shared by every account signing in from the
   * browser. Deliberately not migrated into `assistantHistoryByUser`.
   */
  assistantHistoryCollapsed: boolean;
  /**
   * The Assistant sidebar's History section per User (task 1210), keyed by the
   * Keycloak subject: collapsed, and the list height in px (clamped at read
   * time). Absent entry = open at the default height.
   */
  assistantHistoryByUser: Record<string, { collapsed: boolean; height: number }>;
}

// Default preferences
const defaultPreferences: UIPreferences = {
  projectsViewMode: 'grid',
  specsViewMode: 'grid',
  artifactsViewMode: 'grid',
  personasViewMode: 'grid',
  theme: 'system',
  sidebarCollapsed: false,
  layoutMode: 'standard',
  jsonExpandedByDefault: true,
  dismissedPanels: {},
  // Open by default: an ever-present assistant that starts hidden is just a
  // button, and the point of the surface is that it is already there.
  assistantSidebar: 'open',
  assistantSidebarByUser: {},
  assistantSidebarWidth: 360,
  assistantHistoryCollapsed: false,
  assistantHistoryByUser: {},
};

const UI_PREFERENCES_KEY = 'ui-preferences';

export type ThemePreference = UIPreferences['theme'];

/**
 * The persisted theme preference, read WITHOUT a hook (task 767).
 *
 * The docs-link builders need the preference at call time, from plain functions
 * that are called inside render by components that must keep their current
 * signatures. Reading it here rather than re-deriving the storage key at the
 * call site keeps `UI_PREFERENCES_KEY` owned by exactly one module.
 *
 * Falls back to `system` on absent, malformed, or unreadable storage — the same
 * answer as an untouched account, and the one that carries no theme at all.
 */
export function readStoredThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(UI_PREFERENCES_KEY);
    if (!stored) return defaultPreferences.theme;

    const parsed = JSON.parse(stored) as Partial<UIPreferences>;
    return parsed.theme === 'light' || parsed.theme === 'dark' || parsed.theme === 'system'
      ? parsed.theme
      : defaultPreferences.theme;
  } catch {
    return defaultPreferences.theme;
  }
}

// Custom hook for managing UI preferences
export function useUIPreferences() {
  const [preferences, setPreferences] = useState<UIPreferences>(defaultPreferences);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(UI_PREFERENCES_KEY);
      if (stored) {
        const parsedPreferences = JSON.parse(stored) as Partial<UIPreferences>;
        setPreferences({ ...defaultPreferences, ...parsedPreferences });
      }
    } catch (error) {
      console.warn('Failed to load UI preferences from localStorage:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(UI_PREFERENCES_KEY, JSON.stringify(preferences));
      } catch (error) {
        console.warn('Failed to save UI preferences to localStorage:', error);
      }
    }
  }, [preferences, isLoaded]);

  // Function to update a specific preference
  const updatePreference = <K extends keyof UIPreferences>(
    key: K,
    value: UIPreferences[K]
  ) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // Function to update multiple preferences at once
  const updatePreferences = (updates: Partial<UIPreferences>) => {
    setPreferences(prev => ({
      ...prev,
      ...updates,
    }));
  };

  // Function to reset all preferences to defaults
  const resetPreferences = () => {
    setPreferences(defaultPreferences);
  };

  return {
    preferences,
    updatePreference,
    updatePreferences,
    resetPreferences,
    isLoaded,
  };
}

// Specific hooks for common preferences
export function useProjectsViewMode() {
  const { preferences, updatePreference, isLoaded } = useUIPreferences();
  
  return {
    viewMode: preferences.projectsViewMode,
    setViewMode: (mode: 'grid' | 'list') => updatePreference('projectsViewMode', mode),
    isLoaded,
  };
}

export function useThemePreference() {
  const { preferences, updatePreference, isLoaded } = useUIPreferences();
  
  return {
    theme: preferences.theme,
    setTheme: (theme: 'light' | 'dark' | 'system') => updatePreference('theme', theme),
    isLoaded,
  };
}

export function useSidebarPreference() {
  const { preferences, updatePreference, isLoaded } = useUIPreferences();

  return {
    isCollapsed: preferences.sidebarCollapsed,
    setCollapsed: (collapsed: boolean) => updatePreference('sidebarCollapsed', collapsed),
    isLoaded,
  };
}

export function useSpecsViewMode() {
  const { preferences, updatePreference, isLoaded } = useUIPreferences();

  return {
    viewMode: preferences.specsViewMode,
    setViewMode: (mode: 'grid' | 'list') => updatePreference('specsViewMode', mode),
    isLoaded,
  };
}

export function useArtifactsViewMode() {
  const { preferences, updatePreference, isLoaded } = useUIPreferences();

  return {
    viewMode: preferences.artifactsViewMode,
    setViewMode: (mode: 'grid' | 'list') => updatePreference('artifactsViewMode', mode),
    isLoaded,
  };
}

export function usePersonasViewMode() {
  const { preferences, updatePreference, isLoaded } = useUIPreferences();

  return {
    viewMode: preferences.personasViewMode,
    setViewMode: (mode: 'grid' | 'list') => updatePreference('personasViewMode', mode),
    isLoaded,
  };
}

export function useLayoutMode() {
  const { preferences, updatePreference, isLoaded } = useUIPreferences();

  return {
    layoutMode: preferences.layoutMode,
    setLayoutMode: (mode: 'standard' | 'wide') => updatePreference('layoutMode', mode),
    toggleLayoutMode: () => updatePreference('layoutMode', preferences.layoutMode === 'standard' ? 'wide' : 'standard'),
    isLoaded,
  };
}

export function useJsonExpandPreference() {
  const { preferences, updatePreference, isLoaded } = useUIPreferences();

  return {
    jsonExpandedByDefault: preferences.jsonExpandedByDefault,
    toggleJsonExpand: () => updatePreference('jsonExpandedByDefault', !preferences.jsonExpandedByDefault),
    isLoaded,
  };
}